import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, Heart } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import EstimationBanner from '../../components/EstimationBanner';
import OptimizedImage from '../../components/OptimizedImage';

import SEO from '../../components/SEO';

const ServiceSuccession = () => {
  const advantages = [
    'Intervention respectueuse et discrète',
    'Mise de côté des objets personnels',
    'Coordination avec notaires et héritiers',
    'Gestion administrative facilitée',
    'Accompagnement empathique',
    'Devis gratuit et sans engagement'
  ];

  const accompagnements = [
    {
      title: 'Tri des objets personnels',
      description: 'Nous identifions et mettons de côté les objets à valeur sentimentale pour les héritiers avant toute évacuation.'
    },
    {
      title: 'Coordination avec le notaire',
      description: 'Nous pouvons travailler en coordination avec le notaire en charge de la succession pour faciliter les démarches.'
    },
    {
      title: 'Nettoyage possible',
      description: 'Sur demande, nous proposons plusieurs types de prestations de ménage.'
    },
    {
      title: 'Préparation à la vente',
      description: 'Une fois le débarras effectué, le bien est prêt pour la mise en vente ou la remise aux héritiers.'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Débarras succession et après décès | DEBECO Annecy"
        description="Débarras après décès ou succession en Savoie et Haute-Savoie. Accompagnement empathique, tri des objets personnels, coordination avec notaires."
        canonical="/services/succession"
        ogImage="/images/succession.jpg"
        jsonLd={[
          {"@context":"https://schema.org","@type":"Service","name":"Débarras après succession","description":"Accompagnement respectueux pour le débarras après décès ou succession. Tri des objets personnels, coordination avec notaires et familles.","provider":{"@type":"LocalBusiness","name":"DEBECO","telephone":"+33451401617","address":{"@type":"PostalAddress","addressLocality":"Annecy","postalCode":"74600","addressCountry":"FR"}},"areaServed":[{"@type":"AdministrativeArea","name":"Haute-Savoie"},{"@type":"AdministrativeArea","name":"Savoie"},{"@type":"AdministrativeArea","name":"Ain"}]},
          {"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Accueil","item":"https://debeco.fr/"},{"@type":"ListItem","position":2,"name":"Services","item":"https://debeco.fr/services"},{"@type":"ListItem","position":3,"name":"Débarras succession"}]}
        ]}
      />
      {/* Hero */}
      <section className="hero-banner text-white py-16">
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Débarras succession</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Accompagnement respectueux après un décès
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
              {/* Image */}
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <OptimizedImage 
                  src="/images/succession.jpg"
                  webpSrc="/images/succession.webp"
                  alt="Pièce d'un logement en succession avec des affaires personnelles à trier et débarrasser avec respect"
                  className="w-full h-full object-cover"
                  width="1200"
                  height="800"
                  loading="eager"
                  data-testid="service-succession-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center shadow-lg">
                    <Heart className="w-10 h-10 text-blue-900" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div>
                <h2 className="text-3xl font-bold text-blue-900 mb-6">Accompagnement après décès</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Le débarras d'un logement après le décès d'un proche est une épreuve difficile. Nous comprenons la charge émotionnelle que représente cette étape et nous vous accompagnons avec respect, empathie et discrétion.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  Notre équipe est formée pour gérer ces situations sensibles. Nous prenons le temps de comprendre vos besoins, de trier avec soin les objets personnels que vous souhaitez conserver, et de traiter le reste avec dignité. Vous pouvez compter sur notre professionnalisme et notre humanité.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/contact">
                    <Button className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-lg w-full sm:w-auto">
                      Demander un devis gratuit
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <a href="tel:0451401617">
                    <Button variant="outline" className="border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white w-full sm:w-auto">
                      <Phone className="mr-2 w-4 h-4" />
                      04 51 40 16 17
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Advantages */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">Notre engagement</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {advantages.map((advantage, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{advantage}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Accompagnements */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">Notre accompagnement</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {accompagnements.map((item, index) => (
                  <Card key={index} className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all">
                    <CardContent className="p-6">
                      <h3 className="font-bold text-xl text-blue-900 mb-3">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Estimation Banner */}
      <EstimationBanner />

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Process */}
            <Card className="border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-green-50">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-blue-900 mb-6 text-center">Notre approche</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  {[
                    { step: '1', title: 'Écoute', desc: 'Nous prenons le temps de comprendre vos besoins' },
                    { step: '2', title: 'Tri', desc: 'Mise de côté des souvenirs et objets personnels' },
                    { step: '3', title: 'Débarras', desc: 'Évacuation respectueuse et soignée' },
                    { step: '4', title: 'Remise', desc: 'Logement prêt pour la suite' }
                  ].map((item) => (
                    <div key={item.step} className="text-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-900 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                        <span className="text-xl font-bold text-white">{item.step}</span>
                      </div>
                      <h3 className="font-bold text-blue-900 mb-1">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Maillage interne */}
      <section className="py-12 bg-gray-50" data-testid="maillage-interne">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-blue-900 mb-6">Nos autres services de débarras</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/debarras-succession-annecy" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border-2 border-transparent hover:border-emerald-500">
              <p className="font-semibold text-blue-900">Débarras succession à Annecy</p>
              <p className="text-sm text-gray-600 mt-1">Accompagnement respectueux et coordination notaire</p>
            </Link>
            <Link to="/services/maison" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Débarras de maison complet</p>
              <p className="text-sm text-gray-600 mt-1">Service global du grenier à la cave</p>
            </Link>
            <Link to="/faq" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Questions fréquentes</p>
              <p className="text-sm text-gray-600 mt-1">Comprendre le déroulement et les tarifs</p>
            </Link>
            <Link to="/blog/prix-debarras-maison-annecy" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Guide des prix de débarras</p>
              <p className="text-sm text-gray-600 mt-1">Fourchettes tarifaires détaillées pour Annecy</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-emerald-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Besoin d'un accompagnement pour une succession ?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contactez-nous pour échanger sur votre situation. Nous sommes là pour vous aider.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100 shadow-xl text-lg px-8 py-6 w-full sm:w-auto">
                Nous contacter
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:0451401617">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 w-full sm:w-auto">
                <Phone className="mr-2 w-5 h-5" />
                04 51 40 16 17
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceSuccession;
